import { RunElement } from '../../shared/run-element.js';
import * as defaultSkin from './skins/demo-controls.default.js';
import * as wireframeSkin from './skins/demo-controls.wireframe.js';

// ---------------------------------------------------------------------------
// Exported constants
// ---------------------------------------------------------------------------

export const DEMO_CONTROLS_THEMES = Object.freeze(['light', 'dark']);
export const DEMO_CONTROLS_PAGE_SKINS = Object.freeze(['default', 'wireframe']);
export const DEMO_CONTROLS_POSITIONS = Object.freeze([
  'bottom-right', 'bottom-left', 'top-right', 'top-left',
]);

// ---------------------------------------------------------------------------
// Module-scope CSSStyleSheets (reused across instances)
// ---------------------------------------------------------------------------

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

// ---------------------------------------------------------------------------
// Static localization strings — all locales, loaded once at class definition
// ---------------------------------------------------------------------------

const _STRINGS = {
  en: {
    toolbar_label: 'Design system demo controls',
    theme_to_light: 'Switch to light theme',
    theme_to_dark: 'Switch to dark theme',
    skin_to_wireframe: 'Switch to wireframe skin',
    skin_to_default: 'Switch to default skin',
    collapse: 'Collapse controls',
    expand: 'Expand controls',
    locale_to_next: 'Switch locale to {label}',
    theme_changed: 'Theme changed',
    locale_changed: 'Locale changed',
    skin_changed: 'Skin changed',
    expanded: 'Controls expanded',
    collapsed_msg: 'Controls collapsed',
  },
  es: {
    toolbar_label: 'Controles de demostración del sistema de diseño',
    theme_to_light: 'Cambiar a tema claro',
    theme_to_dark: 'Cambiar a tema oscuro',
    skin_to_wireframe: 'Cambiar a skin wireframe',
    skin_to_default: 'Cambiar a skin predeterminado',
    collapse: 'Contraer controles',
    expand: 'Expandir controles',
    locale_to_next: 'Cambiar idioma a {label}',
    theme_changed: 'Tema cambiado',
    locale_changed: 'Idioma cambiado',
    skin_changed: 'Skin cambiado',
    expanded: 'Controles expandidos',
    collapsed_msg: 'Controles contraídos',
  },
  ar: {
    toolbar_label: 'عناصر التحكم في النظام التصميمي',
    theme_to_light: 'التبديل إلى المظهر الفاتح',
    theme_to_dark: 'التبديل إلى المظهر الداكن',
    skin_to_wireframe: 'التبديل إلى نسق الهيكل',
    skin_to_default: 'التبديل إلى النسق الافتراضي',
    collapse: 'طي عناصر التحكم',
    expand: 'توسيع عناصر التحكم',
    locale_to_next: 'تغيير اللغة إلى {label}',
    theme_changed: 'تم تغيير المظهر',
    locale_changed: 'تم تغيير اللغة',
    skin_changed: 'تم تغيير النسق',
    expanded: 'تم توسيع عناصر التحكم',
    collapsed_msg: 'تم طي عناصر التحكم',
  },
};

const _DEFAULT_LOCALES = Object.freeze([
  { code: 'en', label: 'EN', dir: 'ltr' },
  { code: 'es', label: 'ES', dir: 'ltr' },
  { code: 'ar', label: 'AR', dir: 'rtl' },
]);

// ---------------------------------------------------------------------------
// RunDemoControls
// ---------------------------------------------------------------------------

class RunDemoControls extends RunElement {
  static _instanceCount = 0;

  // Private state
  #theme = 'dark';
  #locale = 'en';
  #pageSkin = 'default';
  #position = 'bottom-right';
  #collapsed = false;
  #locales = [..._DEFAULT_LOCALES];
  #translations = null;
  #rendering = false;

  static get observedAttributes() {
    return ['theme', 'locale', 'page-skin', 'position', 'collapsed'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback?.();
    RunDemoControls._instanceCount++;
    if (RunDemoControls._instanceCount > 1) {
      console.warn(
        'run-demo-controls: multiple instances detected. ' +
        'Only one instance should be active at a time.',
      );
    }
    // Sync from document state
    const docTheme = document.documentElement.dataset.theme;
    if (docTheme && DEMO_CONTROLS_THEMES.includes(docTheme)) {
      this.#theme = docTheme;
      this.setAttribute('theme', docTheme);
    }
    const docSkin = document.documentElement.dataset.runSkin;
    if (docSkin && DEMO_CONTROLS_PAGE_SKINS.includes(docSkin)) {
      this.#pageSkin = docSkin;
      this.setAttribute('page-skin', docSkin);
    }
    const docLang = document.documentElement.lang;
    if (docLang && this.#locales.some(l => l.code === docLang)) {
      this.#locale = docLang;
      this.setAttribute('locale', docLang);
    }
    // Force skin to default unconditionally (MVR D5)
    this.skin = 'default';
    this.shadowRoot.addEventListener('click', this.#handleClick);
    this.shadowRoot.addEventListener('keydown', this.#handleKeydown);
    this.#injectVtStyles();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    RunDemoControls._instanceCount = Math.max(0, RunDemoControls._instanceCount - 1);
    this.shadowRoot.removeEventListener('click', this.#handleClick);
    this.shadowRoot.removeEventListener('keydown', this.#handleKeydown);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'theme':     this.theme    = newValue ?? 'dark'; break;
      case 'locale':    this.locale   = newValue ?? 'en'; break;
      case 'page-skin': this.pageSkin = newValue ?? 'default'; break;
      case 'position':  this.position = newValue ?? 'bottom-right'; break;
      case 'collapsed': this.collapsed = newValue !== null; break;
    }
  }

  // -------------------------------------------------------------------------
  // Property accessors
  // -------------------------------------------------------------------------

  get theme() { return this.#theme; }
  set theme(raw) {
    const coerced = DEMO_CONTROLS_THEMES.includes(raw) ? raw : 'dark';
    if (this.#theme === coerced) return;
    this.#theme = coerced;
    this.setAttribute('theme', coerced);
    document.documentElement.dataset.theme = coerced;
    this.requestUpdate();
  }

  get locale() { return this.#locale; }
  set locale(raw) {
    const match = this.#locales.find(l => l.code === raw);
    const coerced = match ? raw : (this.#locales[0]?.code ?? 'en');
    if (this.#locale === coerced) return;
    this.#locale = coerced;
    this.setAttribute('locale', coerced);
    const entry = this.#locales.find(l => l.code === coerced);
    document.documentElement.lang = coerced;
    document.documentElement.dir = entry?.dir ?? 'ltr';
    this.requestUpdate();
  }

  get pageSkin() { return this.#pageSkin; }
  set pageSkin(raw) {
    const coerced = DEMO_CONTROLS_PAGE_SKINS.includes(raw) ? raw : 'default';
    if (this.#pageSkin === coerced) return;
    this.#pageSkin = coerced;
    this.setAttribute('page-skin', coerced);
    document.documentElement.dataset.runSkin = coerced;
    this.requestUpdate();
  }

  get position() { return this.#position; }
  set position(raw) {
    const coerced = DEMO_CONTROLS_POSITIONS.includes(raw) ? raw : 'bottom-right';
    if (this.#position === coerced) return;
    this.#position = coerced;
    this.setAttribute('position', coerced);
    this.requestUpdate();
  }

  get collapsed() { return this.#collapsed; }
  set collapsed(raw) {
    const coerced = Boolean(raw);
    if (this.#collapsed === coerced) return;
    this.#collapsed = coerced;
    this.toggleAttribute('collapsed', coerced);
    this.requestUpdate();
  }

  get locales() { return this.#locales; }
  set locales(raw) {
    if (!Array.isArray(raw)) return;
    this.#locales = raw.filter(e => e && e.code && e.label && e.dir);
    this.requestUpdate();
  }

  get translations() { return this.#translations; }
  set translations(value) {
    // Passthrough only — never rendered, reflected, or validated
    this.#translations = value;
  }

  /** Exposed for skin modules that need the localization string map. */
  get _strings() { return _STRINGS; }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
      defaultSkin.render(this);
    } finally {
      this.#rendering = false;
    }
  }

  // -------------------------------------------------------------------------
  // Private event handlers (arrow function class fields per consumer_decisions)
  // -------------------------------------------------------------------------

  #handleClick = (e) => {
    const btn = e.target.closest('[data-action]');
    if (!btn) return;
    switch (btn.dataset.action) {
      case 'theme':    this.#onThemeToggle(); break;
      case 'locale':   this.#onLocaleToggle(); break;
      case 'skin':     this.#onSkinToggle(); break;
      case 'collapse': this.#onCollapseToggle(); break;
    }
  };

  #handleKeydown = (e) => {
    // APG Toolbar Pattern: roving tabindex + arrow key navigation
    if (this.#collapsed) return;
    const toolbar = this.shadowRoot.querySelector('[role="toolbar"]');
    if (!toolbar) return;
    const buttons = [...toolbar.querySelectorAll('run-button:not([hidden])')];
    const focusedIdx = buttons.findIndex(
      b => b === document.activeElement || b.contains(document.activeElement),
    );
    switch (e.key) {
      case 'ArrowRight': {
        e.preventDefault();
        if (focusedIdx === -1) return;
        buttons[(focusedIdx + 1) % buttons.length]?.focus();
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        if (focusedIdx === -1) return;
        buttons[(focusedIdx - 1 + buttons.length) % buttons.length]?.focus();
        break;
      }
      case 'Home': {
        e.preventDefault();
        buttons[0]?.focus();
        break;
      }
      case 'End': {
        e.preventDefault();
        buttons[buttons.length - 1]?.focus();
        break;
      }
      case 'Escape': {
        if (!this.#collapsed) {
          e.preventDefault();
          this.#onCollapseToggle();
          this.shadowRoot.querySelector('[data-action="collapse"]')?.focus();
        }
        break;
      }
    }
  };

  // -------------------------------------------------------------------------
  // Private action methods — user-initiated mutations; these fire events
  // -------------------------------------------------------------------------

  #onThemeToggle() {
    const newTheme = this.#theme === 'dark' ? 'light' : 'dark';
    this.#startTransition(() => {
      this.#theme = newTheme;
      this.setAttribute('theme', newTheme);
      document.documentElement.dataset.theme = newTheme;
      this.requestUpdate();
    });
    this.dispatchEvent(new CustomEvent('run-theme-change', {
      bubbles: true,
      composed: true,
      detail: { theme: newTheme },
    }));
    this.#announce((_STRINGS[this.#locale]?.theme_changed) ?? 'Theme changed');
  }

  #onLocaleToggle() {
    if (this.#locales.length === 0) return;
    const idx = this.#locales.findIndex(l => l.code === this.#locale);
    const nextIdx = (idx === -1 ? 0 : (idx + 1)) % this.#locales.length;
    const next = this.#locales[nextIdx];
    if (!next) return;
    this.#locale = next.code;
    this.setAttribute('locale', next.code);
    document.documentElement.lang = next.code;
    document.documentElement.dir = next.dir;
    this.requestUpdate();
    this.dispatchEvent(new CustomEvent('run-locale-change', {
      bubbles: true,
      composed: true,
      detail: { locale: next.code, dir: next.dir, label: next.label },
    }));
    this.#announce((_STRINGS[this.#locale]?.locale_changed) ?? 'Locale changed');
  }

  #onSkinToggle() {
    const newSkin = this.#pageSkin === 'default' ? 'wireframe' : 'default';
    this.#startTransition(() => {
      this.#pageSkin = newSkin;
      this.setAttribute('page-skin', newSkin);
      document.documentElement.dataset.runSkin = newSkin;
      this.requestUpdate();
    });
    this.dispatchEvent(new CustomEvent('run-skin-change', {
      bubbles: true,
      composed: true,
      detail: { skin: newSkin },
    }));
    this.#announce((_STRINGS[this.#locale]?.skin_changed) ?? 'Skin changed');
  }

  #onCollapseToggle() {
    this.#collapsed = !this.#collapsed;
    this.toggleAttribute('collapsed', this.#collapsed);
    this.requestUpdate();
    const msg = this.#collapsed
      ? ((_STRINGS[this.#locale]?.collapsed_msg) ?? 'Controls collapsed')
      : ((_STRINGS[this.#locale]?.expanded) ?? 'Controls expanded');
    this.#announce(msg);
  }

  // -------------------------------------------------------------------------
  // Private utilities
  // -------------------------------------------------------------------------

  #injectVtStyles() {
    if (document.getElementById('run-demo-controls-vt-styles')) return;
    const style = document.createElement('style');
    style.id = 'run-demo-controls-vt-styles';
    style.textContent = [
      '::view-transition-group(root) {',
      '  animation-duration: var(--run-motion-semantic-standard-duration, 0.25s);',
      '  animation-timing-function: var(--run-motion-semantic-standard-easing, cubic-bezier(0.4, 0, 0.2, 1));',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  #startTransition(mutationFn) {
    if (!document.startViewTransition || this.reducedMotion) {
      mutationFn();
      return;
    }
    document.startViewTransition(() => mutationFn());
  }

  /** Clear then set the aria-live announcer. Must run before requestUpdate(). */
  #announce(text) {
    const region = this.shadowRoot?.querySelector('[aria-live="polite"]');
    if (!region) return;
    region.textContent = '';
    region.textContent = text;
  }
}

if (!customElements.get('run-demo-controls')) {
  customElements.define('run-demo-controls', RunDemoControls);
}

export { RunDemoControls };
export default RunDemoControls;
